<script setup lang="ts">
  import { useStorage } from '@vueuse/core'
  import { useFetchFolderFromStoryblok } from '@/composables/fetchFolderFromStoryblok'
  import { cva } from 'class-variance-authority'
  import { pushOffersDataLayer } from '@/utils/dataLayer'
  import { useDebounceFn } from '@vueuse/core'
  import type { HeaderOffersStoryblok } from '@/storyblok/component-types-sb'
  import type { ISbStoryData } from 'storyblok'

  const { locale } = useI18n()

  const isPopoverOpen = ref(false)

  const localOfferStorage = useStorage<string[]>('localOfferStorage', [])

  const resolveRelations = ['offers-block.offers', 'offers.link']
  const slug = 'data/offers'

  const offersStories = await useFetchFolderFromStoryblok<ISbStoryData<HeaderOffersStoryblok>>(slug, resolveRelations)

  const filteredOffers = computed(() => {
    const selectedLang = locale.value === 'en' ? 'default' : locale.value

    return offersStories.filter((offer) => !offer.content?.blackList?.includes(selectedLang))
  })

  const updateSelectedOffer = async (offerSlug: string, offerLink: string, secondaryClick: boolean) => {
    if (!Array.isArray(localOfferStorage.value)) {
      localOfferStorage.value = []
    }

    localOfferStorage.value = [...new Set([...localOfferStorage.value, offerSlug])]

    pushOffersDataLayer({
      eventCategory: 'read_more',
      eventAction: offerSlug
    })

    if (secondaryClick) {
      return
    }

    await navigateTo(offerLink, {
      external: offerLink.startsWith('http')
    })
  }

  const allOffersVisited = computed(() =>
    filteredOffers.value.every((offer) => localOfferStorage.value.some((value) => offer.content.link?.slug === value))
  )

  const offerIndicatorBase = computed(() =>
    cva('relative flex size-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-white', {
      variants: {
        allVisited: {
          true: '',
          false:
            "after:absolute after:right-2.5 after:top-2.5 after:block after:size-2 after:rounded after:bg-bt-red after:content-['']"
        }
      }
    })({
      allVisited: allOffersVisited.value
    })
  )

  const removeOldOffers = () => {
    const activeOffers = filteredOffers.value
      .map((offer) => offer.content.link.slug)
      .filter((slug) => localOfferStorage.value.includes(slug))

    localOfferStorage.value = [...new Set(activeOffers)]
  }

  removeOldOffers()

  const activeOffersCount = computed(() => {
    const activeOffers = filteredOffers.value
      .map((offer) => offer.content.link.slug)
      .filter((slug) => !localOfferStorage.value.includes(slug))

    return activeOffers.length
  })

  const sendOffersEvent = (value?: boolean) => {
    if (!value) return

    pushOffersDataLayer({
      eventCategory: 'click',
      eventAction: activeOffersCount.value
    })
  }

  const debouncedSendOffersEvent = useDebounceFn(sendOffersEvent, 500)

  onMounted(() => {
    if (activeOffersCount.value > 0) {
      pushOffersDataLayer({
        eventCategory: 'see',
        eventAction: activeOffersCount.value
      })
    }
  })
</script>

<template>
  <div>
    <UiPopover
      v-model="isPopoverOpen"
      has-overlay
      :label="$t('aria.label.offers')"
      @update:model-value="debouncedSendOffersEvent"
    >
      <template #trigger>
        <ClientOnly>
          <div :class="offerIndicatorBase">
            <Icon name="common/bell" />
          </div>
        </ClientOnly>
      </template>
      <template #content>
        <div
          class="fixed flex h-full w-full flex-col overflow-auto bg-white p-5 pt-16 sm:relative sm:h-auto sm:min-w-[22.5rem] sm:rounded sm:pt-5"
        >
          <StoryblokComponent
            v-for="offer in filteredOffers"
            :key="offer.content._uid"
            :blok="offer.content"
            class="border-b border-border pb-5 pt-5 last:border-none last:pb-0 sm:first-of-type:pt-0"
            @on-update="updateSelectedOffer"
          />
        </div>
      </template>
      <template #close>
        <div class="relative left-5 top-5 z-10 flex size-6 cursor-pointer">
          <Icon name="common/close" />
        </div>
        <div class="absolute left-0 top-0 h-16 w-full border-b bg-white" />
      </template>
    </UiPopover>
    <UiDarkOverlay
      v-if="isPopoverOpen"
      class="inset-0"
    />
  </div>
</template>
